import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getWeeklyPlanById, openOrCreateWeeklyPlanEditDraft } from "../services/api";
import {
  WEEKLY_PLAN_QUERY_PARAM,
  getWeeklyPlansPath,
  getWeeklyPlanDetailsPath,
} from "../features/weeklyPlans/routes";

// Makes a persisted weekly plan reloadable. The weekly builder and its workout
// editor keep the plan in memory; the plan identity travels in the URL as
// `?plan=<weeklyPlanParentId>` (added to the URL, with replace, as soon as a
// persisted plan is loaded). On a hard reload / deep link the in-memory plan is
// not the one in the URL, and this hook restores it before the page evaluates
// any redirect guard.
//
// Lifecycle safety: the draft is opened only after a read-only check
// (getWeeklyPlanById.latestDraftVersionId) shows the plan ALREADY has an
// editable draft -- the open call is "open or create", so it must never be the
// first thing to find out. A published plan without a draft, a missing plan or
// any failure leaves for the read-only plan views; nothing is created, nothing
// local is saved over the stored plan.
// `manual` is the page's ManualProgram context value (null when the page runs
// without the provider, which makes this hook inert).
// `enabled: false` makes the hook inert too, for a route that does not belong to a
// weekly plan. The Manual context lives above the router, so after a Weekly plan is
// converted its metadata is still in memory on Multi-Week (cycle) routes: without
// this, the hook would write `?plan=<weeklyPlanId>` into the cycle editor URL and
// a reload would restore (and leave for) the old Weekly plan. Such a route also
// ignores a stale `?plan=` it was given.
export function useWeeklyPlanRestore(manual, { enabled = true } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [failure, setFailure] = useState(null);
  const attemptedRef = useRef(null);
  // Provider values change every render and StrictMode mounts effects twice:
  // neither may cancel a restore. Only leaving this plan drops its result.
  const manualRef = useRef(manual);
  manualRef.current = manual;

  const planId = enabled
    ? new URLSearchParams(location.search).get(WEEKLY_PLAN_QUERY_PARAM) || null
    : null;
  const planIdRef = useRef(planId);
  planIdRef.current = planId;

  const metadata = manual?.draftMetadata;
  const loadedPlanId =
    enabled && metadata?.loadedFromBackend ? metadata.weeklyPlanParentId || null : null;
  // The context already holds this plan (e.g. its identity was just written into the
  // URL by the effect below): nothing to restore, whatever its status.
  const alreadyLoaded = Boolean(planId && loadedPlanId === planId);
  const failed = Boolean(planId) && failure?.planId === planId;
  const isRestoring = Boolean(planId) && !alreadyLoaded && !failed;
  // True for the whole life of a screen that was opened with a plan that was not
  // loaded yet (reload / deep link): its workout id must match exactly.
  const openedByRestoreRef = useRef(isRestoring);

  useEffect(() => {
    if (!isRestoring || attemptedRef.current === planId) {
      return;
    }
    attemptedRef.current = planId;
    const isCurrent = () => planIdRef.current === planId;
    const fail = (leaveTo) => {
      if (isCurrent()) setFailure({ planId, leaveTo });
    };

    async function restore() {
      try {
        const details = await getWeeklyPlanById(planId);
        if (!details?.latestDraftVersionId) {
          fail(getWeeklyPlanDetailsPath(planId));
          return;
        }
        // Declaring a target is not harmless: only a still-current URL may do it.
        if (!isCurrent()) return;
        await manualRef.current.beginHydrationTarget({
          weeklyPlanParentId: planId,
          weeklyPlanVersionId: null,
        });
        // The precondition makes the backend refuse (409, no writes) instead of
        // creating a draft if this one vanished since the read-only check.
        const response = await openOrCreateWeeklyPlanEditDraft(planId, {
          expectedDraftVersionId: details.latestDraftVersionId,
        });
        if (!isCurrent()) return;
        if (response?.weeklyPlanVersionId !== details.latestDraftVersionId) {
          fail(getWeeklyPlanDetailsPath(planId));
          return;
        }
        manualRef.current.hydrateProgramDraft(response);
      } catch (error) {
        // 404: no such plan. 409 (draft changed) and anything else: the read-only view.
        fail(error?.status === 404 ? getWeeklyPlansPath() : getWeeklyPlanDetailsPath(planId));
      }
    }

    restore();
  }, [isRestoring, planId]);

  // Persisted plan loaded but the URL does not say which: write it in place.
  useEffect(() => {
    if (!planId && loadedPlanId) {
      const params = new URLSearchParams(location.search);
      params.set(WEEKLY_PLAN_QUERY_PARAM, loadedPlanId);
      navigate(
        { pathname: location.pathname, search: `?${params.toString()}` },
        { replace: true, state: location.state }
      );
    }
  }, [loadedPlanId, location.pathname, location.search, location.state, navigate, planId]);

  // Nothing editable to restore: leave for the read-only views.
  // Once per failure: `navigate` changes identity with every location change,
  // so this must not re-fire if the screen is still mounted afterwards.
  const leftForRef = useRef(null);
  useEffect(() => {
    if (failed && failure.leaveTo && leftForRef.current !== failure) {
      leftForRef.current = failure;
      navigate(failure.leaveTo, { replace: true });
    }
  }, [failed, failure, navigate]);

  return {
    isRestoring,
    isUnavailable: failed,
    planId,
    openedByRestore: openedByRestoreRef.current,
  };
}
